import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { HelpCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuestionsPage() {
  const { user, getAuthHeaders } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    full_name: user?.name || '',
    email: user?.email || '',
    course_id: '',
    subject: '',
    message: ''
  });

  const fetchCourses = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API_URL}/api/courses`, { headers });
      setCourses(res.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        full_name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const selectableCourses = useMemo(() => courses.filter(course => !course.is_capstone), [courses]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.course_id) {
      toast.error('Please select a course');
      return;
    }
    if (!formData.subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!formData.message.trim()) {
      toast.error('Please enter your question');
      return;
    }

    setSubmitting(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API_URL}/api/questions`, {
        full_name: formData.full_name,
        email: formData.email,
        course_id: formData.course_id,
        subject: formData.subject,
        message: formData.message
      }, { headers });

      toast.success('Your question has been submitted successfully!');
      setFormData(prev => ({
        ...prev,
        course_id: '',
        subject: '',
        message: ''
      }));
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit question';
      toast.error(typeof message === 'string' ? message : 'Failed to submit question');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen pt-14 lg:pt-0">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-background">
          <div className="animate-pulse">Loading...</div>
        </main>
      </div>
    );
  }

  const isVisitor = user?.role === 'visitor';

  return (
    <div className="flex min-h-screen pt-14 lg:pt-0" data-testid="questions-page">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-background overflow-auto">
        {/* Visitor Banner */}
        {isVisitor && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-sm" data-testid="visitor-banner">
            <p className="text-amber-800 text-sm font-medium">You are viewing this page as a visitor. Form submissions are disabled.</p>
          </div>
        )}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-primary mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Questions
          </h1>
          <p className="text-muted-foreground">
            Having difficulty with a lecture? Submit your question and we will get back to you.
          </p>
        </div>

        <Card className="border border-border shadow-none max-w-2xl" data-testid="question-form">
          <CardHeader>
            <CardTitle className="text-xl font-medium flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <HelpCircle className="w-5 h-5" />
              Ask a Question
            </CardTitle>
            <CardDescription>
              Fill in the form below to submit your question about a specific course or lecture.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="q_full_name" className="text-sm font-medium">
                  Full Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="q_full_name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  data-testid="question-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="q_email" className="text-sm font-medium">
                  Email Address <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="q_email"
                  type="email"
                  placeholder="your.email@gihds.org"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="question-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="q_course" className="text-sm font-medium">
                  Course <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.course_id}
                  onValueChange={(value) => setFormData({ ...formData, course_id: value })}
                >
                  <SelectTrigger data-testid="question-course-select">
                    <SelectValue placeholder="Select the course related to your question" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectableCourses.map((course) => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.order}. {course.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="q_subject" className="text-sm font-medium">
                  Subject <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="q_subject"
                  type="text"
                  placeholder="Brief subject of your question"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  data-testid="question-subject-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="q_message" className="text-sm font-medium">
                  Your Question <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="q_message"
                  placeholder="Describe your question or difficulty in detail..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={8}
                  className="min-h-[200px] resize-y"
                  data-testid="question-message-input"
                />
                <p className="text-xs text-muted-foreground">
                  {formData.message.length} characters
                </p>
              </div>

              <Button
                type="submit"
                disabled={submitting || isVisitor}
                className="w-full h-11"
                data-testid="submit-question-button"
              >
                {submitting ? (
                  <>Submitting...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Question
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
